import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function FormsPage(): Node {
  return html`
    <div>
      <h1>Forms</h1>
      <p><code>createForm</code> provides reactive form management with field-level state tracking, validation, dirty/touched states, and submission handling.</p>

      <h2>Basic Usage</h2>
      ${CodeBlock(`import { createForm, required, email } from 'onefold/form';

const form = createForm({
  fields: {
    name: { initial: '', rules: [required()] },
    email: { initial: '', rules: [required(), email()] },
    age: { initial: 18, rules: [] },
  },
  onSubmit: (values) => {
    console.log('Submitted:', values);
  },
});`)}

      <h2>Binding to Templates</h2>
      ${CodeBlock(`function ContactForm(): Node {
  const form = createForm({
    fields: {
      name: { initial: '', rules: [required()] },
      email: { initial: '', rules: [required(), email()] },
      message: { initial: '', rules: [required()] },
    },
    onSubmit: async (values) => {
      await fetch('/api/contact', {
        method: 'POST',
        body: JSON.stringify(values),
      });
    },
  });

  return html\`
    <form onsubmit=\${form.handleSubmit}>
      <div>
        <label>Name</label>
        <input
          value=\${() => form.field('name').value()}
          oninput=\${(e: Event) => form.field('name').set((e.target as HTMLInputElement).value)}
          onblur=\${() => form.field('name').touch()}
        />
        \${() => form.field('name').error()
          ? html\`<span class="error">\${form.field('name').error()}</span>\`
          : html\`<span></span>\`
        }
      </div>

      <button type="submit" disabled=\${() => !form.valid()}>
        Submit
      </button>
    </form>
  \`;
}`)}

      <h2>Field API</h2>
      <table>
        <tr><th>Property/Method</th><th>Type</th><th>Description</th></tr>
        <tr><td><code>.value()</code></td><td>T</td><td>Current field value (reactive).</td></tr>
        <tr><td><code>.set(value)</code></td><td>void</td><td>Update the field value.</td></tr>
        <tr><td><code>.error()</code></td><td>string | null</td><td>First validation error (reactive).</td></tr>
        <tr><td><code>.errors()</code></td><td>string[]</td><td>All validation errors (reactive).</td></tr>
        <tr><td><code>.touched()</code></td><td>boolean</td><td>True after user interaction.</td></tr>
        <tr><td><code>.touch()</code></td><td>void</td><td>Mark field as touched.</td></tr>
        <tr><td><code>.dirty()</code></td><td>boolean</td><td>True if value differs from initial.</td></tr>
        <tr><td><code>.reset()</code></td><td>void</td><td>Reset to initial value.</td></tr>
      </table>

      <h2>Form API</h2>
      <table>
        <tr><th>Property/Method</th><th>Type</th><th>Description</th></tr>
        <tr><td><code>form.valid()</code></td><td>boolean</td><td>True when all fields pass validation.</td></tr>
        <tr><td><code>form.dirty()</code></td><td>boolean</td><td>True when any field is dirty.</td></tr>
        <tr><td><code>form.field(name)</code></td><td>Field</td><td>Access a field's reactive state.</td></tr>
        <tr><td><code>form.handleSubmit</code></td><td>(e: Event) => void</td><td>Submit handler (prevents default, validates, calls onSubmit).</td></tr>
        <tr><td><code>form.reset()</code></td><td>void</td><td>Reset all fields to initial values.</td></tr>
        <tr><td><code>form.values()</code></td><td>Record</td><td>Current values of all fields.</td></tr>
      </table>

      ${Callout('Validation runs on every .set() call. Errors are reactive — your UI updates automatically when a field becomes valid or invalid.')}

      ${Playground(`import { html, mount } from 'onefold';
import { createForm, required, email, minLength } from 'onefold/form';

function App() {
  const form = createForm({
    name: { initial: '', rules: [required('Name is required'), minLength(2, 'At least 2 characters')] },
    email: { initial: '', rules: [required('Email is required'), email('Must be a valid email')] },
    password: { initial: '', rules: [required('Password is required'), minLength(6, 'At least 6 characters')] },
  });

  const submitted = { value: false, data: null };

  function handleSubmit(values) {
    submitted.value = true;
    submitted.data = values;
  }

  return html\`
    <div>
      <h3>Registration Form — createForm</h3>
      <form onsubmit=\${(e) => { e.preventDefault(); form.submit(handleSubmit); }}>
        <div style="margin-bottom:14px">
          <label style="display:block;font-size:13px;font-weight:600;margin-bottom:4px">Name</label>
          <input
            type="text"
            oninput=\${form.fields.name.handle}
            placeholder="Your name"
            style="width:100%"
          />
          \${() => form.fields.name.error() ? html\`<p style="color:#dc2626;font-size:12px;margin-top:4px">\${form.fields.name.error()}</p>\` : null}
        </div>
        <div style="margin-bottom:14px">
          <label style="display:block;font-size:13px;font-weight:600;margin-bottom:4px">Email</label>
          <input
            type="email"
            oninput=\${form.fields.email.handle}
            placeholder="you@example.com"
            style="width:100%"
          />
          \${() => form.fields.email.error() ? html\`<p style="color:#dc2626;font-size:12px;margin-top:4px">\${form.fields.email.error()}</p>\` : null}
        </div>
        <div style="margin-bottom:14px">
          <label style="display:block;font-size:13px;font-weight:600;margin-bottom:4px">Password</label>
          <input
            type="password"
            oninput=\${form.fields.password.handle}
            placeholder="Min 6 characters"
            style="width:100%"
          />
          \${() => form.fields.password.error() ? html\`<p style="color:#dc2626;font-size:12px;margin-top:4px">\${form.fields.password.error()}</p>\` : null}
        </div>
        <div style="display:flex;gap:8px;align-items:center">
          <button type="submit">Register</button>
          <button type="button" onclick=\${() => form.reset()}>Reset</button>
          <span style="font-size:12px;color:#666;margin-left:auto">
            \${() => form.valid() ? '✓ Valid' : '✗ Invalid'} · \${() => form.dirty() ? 'Modified' : 'Pristine'}
          </span>
        </div>
      </form>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'createForm — Registration with Validation')}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/forms/validation">Validation Rules</a> — built-in and custom validation rules</li>
        <li><a href="/data/http-client">HTTP Client</a> — submit form data to your API</li>
      </ul>
    </div>
  `;
}
